import { useEffect, useState, useMemo } from "react";
import { fetchProducts, CatalogProduct } from "@/lib/catalog";
import { VariantCard } from "./VariantCard";

interface FlatVariant {
  product: CatalogProduct;
  variantIdx: number;
  variantId: string;
  productHandle: string;
}

interface RecommendationCarouselProps {
  title: string;
  /** Variant IDs to exclude (e.g. current PDP variant or items already in cart). */
  excludeVariantIds?: string[];
  /**
   * "cross-sell": prioritize variants from OTHER products (relative to currentProductHandle),
   * then backfill with same-product variants.
   * "recently-viewed": filter the catalog down to variantIds in `orderedVariantIds`, preserving order.
   */
  mode: "cross-sell" | "recently-viewed";
  currentProductHandle?: string;
  orderedVariantIds?: string[];
  max?: number;
  className?: string;
}

export const RecommendationCarousel = ({
  title,
  excludeVariantIds = [],
  mode,
  currentProductHandle,
  orderedVariantIds = [],
  max = 8,
  className = "",
}: RecommendationCarouselProps) => {
  const [allProducts, setAllProducts] = useState<CatalogProduct[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchProducts()
      .then((products) => {
        if (!cancelled) setAllProducts(products);
      })
      .catch(console.error)
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const items = useMemo<FlatVariant[]>(() => {
    if (!allProducts.length) return [];
    const exclude = new Set(excludeVariantIds);
    const flat: FlatVariant[] = [];
    for (const product of allProducts) {
      product.node.variants.edges.forEach((edge, idx) => {
        const v = edge.node;
        if (!v.availableForSale) return;
        if (exclude.has(v.id)) return;
        flat.push({
          product,
          variantIdx: idx,
          variantId: v.id,
          productHandle: product.node.handle,
        });
      });
    }

    if (mode === "recently-viewed") {
      const order = new Map(orderedVariantIds.map((id, i) => [id, i]));
      return flat
        .filter((f) => order.has(f.variantId))
        .sort((a, b) => (order.get(a.variantId)! - order.get(b.variantId)!))
        .slice(0, max);
    }

    // cross-sell: other products first, then same-product variants
    const other = flat.filter((f) => f.productHandle !== currentProductHandle);
    const same = flat.filter((f) => f.productHandle === currentProductHandle);
    return [...other, ...same].slice(0, max);
  }, [allProducts, excludeVariantIds, mode, currentProductHandle, orderedVariantIds, max]);

  if (!loaded || items.length === 0) return null;

  return (
    <section className={className}>
      <h2 className="font-display text-2xl md:text-3xl font-semibold mb-4">{title}</h2>
      <div className="-mx-4 px-4 overflow-x-auto scrollbar-thin">
        <div className="flex gap-4 pb-2">
          {items.map((item) => (
            <div
              key={item.variantId}
              className="flex-shrink-0 w-[60vw] sm:w-64 md:w-72"
            >
              <VariantCard product={item.product} variantIdx={item.variantIdx} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
